import { useApiUrl, useDataProvider } from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import {
  CancelledCandidate,
  CancelledEventDoc,
  DeleteResult,
  deleteAll,
  searchAllPages,
  toCandidates,
} from "@/pages/events/features/delete-cancelled/cancelled-events";

const PER_PAGE = 250;

// Typesense matches an optional bool only on documents that carry the field,
// so this selects exactly the events with a ticketing block. `/explore`
// returns no `ticketing`, so the filter is the only way to see it.
const HAS_TICKETING = "ticketing.available:[true,false]";

// Typesense tries a typo only when the exact word finds nothing, so a search
// for "cancelled" never returns "CANCELED - Grace Enger". Search both.
const SPELLINGS = ["cancelled", "canceled"];

export const useCancelledEvents = () => {
  const apiUrl = useApiUrl();
  const { custom, deleteOne } = useDataProvider()();

  const searchOne = (q: string, filters: string) =>
    searchAllPages<CancelledEventDoc>(async (page) => {
      const response = await custom!({
        url: `${apiUrl}/events/explore`,
        method: "get",
        query: {
          q,
          queryBy: "name",
          limit: PER_PAGE,
          page,
          filters,
          sortBy: "_text_match:desc",
          disableCaching: true,
        },
      });
      return {
        found: response.data?.found ?? 0,
        hits: (response.data?.hits ?? []).map(
          (hit: { document: CancelledEventDoc }) => hit.document,
        ),
      };
    }, PER_PAGE);

  const search = async (filters: string) =>
    (await Promise.all(SPELLINGS.map((q) => searchOne(q, filters)))).flat();

  async function find(): Promise<CancelledCandidate[]> {
    const [docs, ticketed] = await Promise.all([
      search(""),
      search(HAS_TICKETING),
    ]);
    return toCandidates(docs, new Set(ticketed.map((d) => d.id)));
  }

  function remove(
    ids: string[],
    onProgress: (done: number) => void,
  ): Promise<DeleteResult> {
    return deleteAll(
      ids,
      async (id) => {
        try {
          await deleteOne({ resource: "events", id });
        } catch (error) {
          throw new Error(toHumanErrorMessage(error));
        }
      },
      { onProgress },
    );
  }

  return { find, remove };
};
